import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Snackbar,
} from "@mui/material";
import ArrowUpwardOutlinedIcon from "@mui/icons-material/ArrowUpwardOutlined";
import ArrowDownwardOutlinedIcon from "@mui/icons-material/ArrowDownwardOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import MinimizeOutlinedIcon from "@mui/icons-material/MinimizeOutlined";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import CancelPresentationOutlinedIcon from "@mui/icons-material/CancelPresentationOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import UpdateOutlinedIcon from "@mui/icons-material/UpdateOutlined";
import MergeTypeOutlinedIcon from "@mui/icons-material/MergeTypeOutlined";
import FloatingPanelWindow, {
  FloatingPanelDock,
  FloatingPanelDockTray,
} from "./FloatingPanelWindow";
import DateRangeFilter from "./DateRangeFilter";
import {
  addJobToAssignmentPeriod,
  addJobsToAssignmentPeriod,
  createAssignmentPeriod,
  deleteAssignmentPeriod,
  estimateAssignmentPeriodEndTime,
  getAssignmentPeriodJobs,
  getAssignmentPeriods,
  getAssignmentPeriodsByJob,
  mergeAssignmentPeriods,
  removeJobFromAssignmentPeriod,
  reorderAssignmentPeriodJobs,
  updateAssignmentPeriod,
} from "../../api/jobAssignmentPeriods";
import { getRoomRestrictionColor } from "../../utils/roomRestriction";
import { DeleteForeverOutlined } from "@mui/icons-material";

const PERIOD_STATUS_OPTIONS = ["draft", "active", "completed", "cancelled"];
const PERIOD_WINDOW_WIDTH = 560;
const ROUTES_TABLE_MIN_HEIGHT_10_ROWS = 560;

const getPeriodStatusTone = (status) => {
  switch (String(status || "").toLowerCase()) {
    case "active":
      return { backgroundColor: "#e8f2ff", color: "#1d5f99" };
    case "draft":
      return { backgroundColor: "#fff7dc", color: "#7a5b00" };
    case "cancelled":
      return { backgroundColor: "#fdecea", color: "#8a2d24" };
    case "completed":
      return { backgroundColor: "#e8f5ee", color: "#23613b" };
    default:
      return { backgroundColor: "#eef2f4", color: "#41515c" };
  }
};

const toInputDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const compactDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString([], {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const parseIdOrNull = (value) => {
  if (value === "" || value === null || value === undefined) {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toDateKey = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const canAssignToRoute = (job) => {
  const status = String(job?.status || "").toLowerCase();
  return status === "pending" || status === "assigned";
};

const assignRouteStatusWarning = "Can ONLY assign pending or assigned jobs";
const BULK_NO_CHANGE = "__no_change__";
const BULK_UNASSIGNED = "__unassigned__";

export default function JobsAssignmentPanel({
  visibleJobs = [],
  masterRangeStart = "",
  masterRangeEnd = "",
  masterRangeSignal = 0,
  selectedJobId,
  technicianOptions = [],
  user,
  isAdmin,
  hasMinimizedJobWindows = false,
  onPeriodSelectionChange,
  clearSelectionSignal = 0,
  onAssignmentsChanged,
  onPeriodDeleted,
  customer = false,
  customerId = "",
  pickerActive = false,
  onPeriodPicked,
  onCancelPeriodPicker,
  queueSelectedJobIds = [],
}) {
  const isCustomer = Boolean(customer);
  const canEditRoutes = Boolean(isAdmin || isCustomer);
  const [periods, setPeriods] = useState([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState("");
  const [periodJobs, setPeriodJobs] = useState([]);
  const [selectedJobPeriods, setSelectedJobPeriods] = useState([]);
  const [feedback, setFeedback] = useState({ type: "info", message: "" });
  const [busy, setBusy] = useState(false);
  const [periodSearch, setPeriodSearch] = useState("");
  const [periodStatusFilter, setPeriodStatusFilter] = useState("all");
  const [periodAssigneeFilter, setPeriodAssigneeFilter] = useState("all");
  const [routeAssignmentFilter, setRouteAssignmentFilter] = useState("all");
  const [periodRangeStart, setPeriodRangeStart] = useState("");
  const [periodRangeEnd, setPeriodRangeEnd] = useState("");
  const [selectedPeriodIds, setSelectedPeriodIds] = useState([]);
  const [bulkStatus, setBulkStatus] = useState(BULK_NO_CHANGE);
  const [bulkAssignee, setBulkAssignee] = useState(BULK_NO_CHANGE);
  const [selectionAnchorPeriodId, setSelectionAnchorPeriodId] = useState(null);
  const [mergePickerOpen, setMergePickerOpen] = useState(false);
  const [mergePendingPeriodIds, setMergePendingPeriodIds] = useState([]);
  const [mergeTargetUserId, setMergeTargetUserId] = useState("");
  const [merging, setMerging] = useState(false);
  const [routesPage, setRoutesPage] = useState(0);
  const [routesRowsPerPage, setRoutesRowsPerPage] = useState(50);
  const [periodDetailsOpen, setPeriodDetailsOpen] = useState(false);
  const [periodDetailsMinimized, setPeriodDetailsMinimized] = useState(false);
  const [periodWindowPos, setPeriodWindowPos] = useState({ x: 16, y: 90 });
  const [periodWindowDrag, setPeriodWindowDrag] = useState({
    active: false,
    offsetX: 0,
    offsetY: 0,
  });
  const [createPeriodOpen, setCreatePeriodOpen] = useState(false);
  const [createPeriodMinimized, setCreatePeriodMinimized] = useState(false);
  const [createPeriodWindowPos, setCreatePeriodWindowPos] = useState({
    x: 24,
    y: 120,
  });
  const [createPeriodWindowDrag, setCreatePeriodWindowDrag] = useState({
    active: false,
    offsetX: 0,
    offsetY: 0,
  });
  const [estimatedJobWindows, setEstimatedJobWindows] = useState({});

  const selectMenuProps = useMemo(
    () => ({
      MenuProps: {
        sx: { zIndex: 4000 },
        PaperProps: {
          sx: { zIndex: 4000 },
        },
      },
    }),
    [],
  );

  const selectedPeriodIdRef = React.useRef(selectedPeriodId);
  React.useEffect(() => {
    selectedPeriodIdRef.current = selectedPeriodId;
  }, [selectedPeriodId]);

  // Keeps the ref in sync immediately so async flows (e.g. create/delete)
  // never read a stale selectedPeriodId before the next render commits.
  const selectPeriodId = useCallback((nextId) => {
    selectedPeriodIdRef.current = nextId;
    setSelectedPeriodId(nextId);
  }, []);

  const getDefaultCreatePeriodStart = () => {
    if (periodRangeStart) {
      return `${periodRangeStart}T00:00`;
    }

    return toInputDateTime(new Date());
  };

  const [newPeriodDraft, setNewPeriodDraft] = useState({
    assigned_user_id: "",
    period_start: getDefaultCreatePeriodStart(),
    period_end: "",
    label: "default",
    status: "draft",
    notes: "",
  });

  const [periodEditDraft, setPeriodEditDraft] = useState({
    assigned_user_id: "",
    period_start: "",
    period_end: "",
    label: "",
    status: "draft",
    notes: "",
  });

  const selectedPeriod = useMemo(
    () =>
      periods.find(
        (period) => String(period.pk_id) === String(selectedPeriodId),
      ) || null,
    [periods, selectedPeriodId],
  );

  const originalPeriodEditDraft = useMemo(() => {
    if (!selectedPeriod) {
      return null;
    }

    return {
      assigned_user_id: String(selectedPeriod.assigned_user_id || ""),
      period_start: toInputDateTime(selectedPeriod.period_start),
      period_end: toInputDateTime(selectedPeriod.period_end),
      label: selectedPeriod.label || "default",
      status: selectedPeriod.status || "draft",
      notes: selectedPeriod.notes || "",
    };
  }, [selectedPeriod]);

  const hasPeriodEditChanges = useMemo(() => {
    if (!originalPeriodEditDraft) {
      return false;
    }

    return (
      periodEditDraft.assigned_user_id !==
        originalPeriodEditDraft.assigned_user_id ||
      periodEditDraft.period_start !== originalPeriodEditDraft.period_start ||
      periodEditDraft.period_end !== originalPeriodEditDraft.period_end ||
      periodEditDraft.label !== originalPeriodEditDraft.label ||
      periodEditDraft.status !== originalPeriodEditDraft.status ||
      periodEditDraft.notes !== originalPeriodEditDraft.notes
    );
  }, [originalPeriodEditDraft, periodEditDraft]);

  const assigneeLabelById = useMemo(() => {
    const map = new Map();
    technicianOptions.forEach((item) => {
      if (!item.value || item.value === "__unassigned__") return;
      map.set(String(item.value), item.label || String(item.value));
    });
    return map;
  }, [technicianOptions]);

  const periodAssigneeOptions = useMemo(() => {
    const ids = Array.from(
      new Set(periods.map((period) => period.assigned_user_id).filter(Boolean)),
    ).map(String);

    return ids
      .map((id) => ({
        value: id,
        label: assigneeLabelById.get(id) || `User ${id}`,
      }))
      .sort((left, right) => left.label.localeCompare(right.label));
  }, [assigneeLabelById, periods]);

  const routeFilterOptions = useMemo(() => {
    const labels = new Map();

    periods.forEach((period) => {
      const routeLabel = period.route_name || "No Route";
      if (routeLabel) {
        labels.set(String(routeLabel), routeLabel);
      }
    });

    return [
      { value: "all", label: "All routes" },
      ...Array.from(labels.values())
        .sort((left, right) => left.localeCompare(right))
        .map((label) => ({ value: label, label })),
    ];
  }, [periods]);

  const filteredPeriods = useMemo(() => {
    const term = periodSearch.trim().toLowerCase();
    const normalizedStart = periodRangeStart || "";
    const normalizedEnd = periodRangeEnd || "";
    const hasRange = Boolean(normalizedStart || normalizedEnd);

    return periods.filter((period) => {
      const status = String(period.status || "").toLowerCase();
      const assigneeId = period.assigned_user_id
        ? String(period.assigned_user_id)
        : "";
      const routeLabel = period.route_name || period.label || "No Route";
      const periodDateKey = toDateKey(period.period_start || period.created_at);
      const assigneeLabel = assigneeId
        ? assigneeLabelById.get(assigneeId) || `User ${assigneeId}`
        : "Unassigned";

      const matchesSearch =
        !term ||
        [
          `#${period.pk_id}`,
          period.label,
          period.notes,
          status,
          assigneeLabel,
          assigneeId,
          routeLabel,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term));

      const matchesStatus =
        periodStatusFilter === "all" || status === String(periodStatusFilter);

      const matchesAssignee =
        periodAssigneeFilter === "all" ||
        (periodAssigneeFilter === "unassigned"
          ? !assigneeId
          : assigneeId === periodAssigneeFilter);

      const matchesRoute =
        routeAssignmentFilter === "all" || routeLabel === routeAssignmentFilter;

      const matchesRange =
        !hasRange ||
        (periodDateKey &&
          (!normalizedStart || periodDateKey >= normalizedStart) &&
          (!normalizedEnd || periodDateKey <= normalizedEnd));

      return (
        matchesSearch &&
        matchesStatus &&
        matchesAssignee &&
        matchesRoute &&
        matchesRange
      );
    });
  }, [
    assigneeLabelById,
    periodAssigneeFilter,
    periodRangeEnd,
    periodRangeStart,
    periodSearch,
    periodStatusFilter,
    periods,
    routeAssignmentFilter,
  ]);

  const paginatedRoutes = useMemo(() => {
    const start = routesPage * routesRowsPerPage;
    return filteredPeriods.slice(start, start + routesRowsPerPage);
  }, [filteredPeriods, routesPage, routesRowsPerPage]);

  const selectedPeriodIdsSet = useMemo(
    () => new Set(selectedPeriodIds.map(String)),
    [selectedPeriodIds],
  );
  const paginatedRouteIds = useMemo(
    () => paginatedRoutes.map((period) => String(period.pk_id)),
    [paginatedRoutes],
  );
  const allVisiblePageSelected =
    paginatedRoutes.length > 0 &&
    paginatedRouteIds.every((periodId) => selectedPeriodIdsSet.has(periodId));
  const someVisiblePageSelected = paginatedRouteIds.some((periodId) =>
    selectedPeriodIdsSet.has(periodId),
  );
  const selectedPeriodCount = selectedPeriodIds.length;

  useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil(filteredPeriods.length / routesRowsPerPage) - 1,
    );

    if (routesPage > maxPage) {
      setRoutesPage(maxPage);
    }
  }, [filteredPeriods.length, routesPage, routesRowsPerPage]);

  const handleRoutesPageChange = (_, nextPage) => {
    setRoutesPage(nextPage);
  };

  const handleRoutesRowsPerPageChange = (event) => {
    setRoutesRowsPerPage(parseInt(event.target.value, 10));
    setRoutesPage(0);
  };

  const loadPeriods = useCallback(async () => {
    const response = await getAssignmentPeriods({
      include_draft: isAdmin,
      customer_id: customerId,
    });
    console.log("Assignment periods API response:", response);
    const list = Array.isArray(response?.periods) ? response.periods : [];
    setPeriods(list);

    if (list.length === 0) {
      selectPeriodId("");
      return;
    }

    const currentSelected = selectedPeriodIdRef.current;
    if (
      currentSelected &&
      !list.some((p) => String(p.pk_id) === String(currentSelected))
    ) {
      selectPeriodId("");
    }
  }, [customerId, isAdmin, selectPeriodId]);

  const loadPeriodJobs = useCallback(async () => {
    const targetPeriodId = selectedPeriodIdRef.current;
    if (!targetPeriodId) {
      setPeriodJobs([]);
      setEstimatedJobWindows({});
      return;
    }

    const response = await getAssignmentPeriodJobs(targetPeriodId, {
      include_draft: isAdmin,
    });

    // Discard stale results if the selected period changed while this was in flight.
    if (selectedPeriodIdRef.current !== targetPeriodId) {
      return;
    }

    setPeriodJobs(Array.isArray(response?.jobs) ? response.jobs : []);
  }, [isAdmin, selectedPeriodId]);

  const loadSelectedJobPeriods = useCallback(async () => {
    if (!selectedJobId) {
      setSelectedJobPeriods([]);
      return;
    }

    const response = await getAssignmentPeriodsByJob(selectedJobId, {
      include_draft: isAdmin,
    });
    setSelectedJobPeriods(
      Array.isArray(response?.periods) ? response.periods : [],
    );
  }, [isAdmin, selectedJobId]);

  const refreshAll = useCallback(async () => {
    try {
      setBusy(true);
      setFeedback({ type: "info", message: "" });
      await loadPeriods();
    } catch (err) {
      setFeedback({
        type: "error",
        message: err.message || "Failed to load assignment periods",
      });
    } finally {
      setBusy(false);
    }
  }, [loadPeriods]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  useEffect(() => {
    if (!selectedPeriod) {
      setPeriodEditDraft({
        assigned_user_id: "",
        period_start: "",
        period_end: "",
        label: "",
        status: "draft",
        notes: "",
      });
      return;
    }

    setPeriodEditDraft({
      assigned_user_id: String(selectedPeriod.assigned_user_id || ""),
      period_start: toInputDateTime(selectedPeriod.period_start),
      period_end: toInputDateTime(selectedPeriod.period_end),
      label: selectedPeriod.label || "default",
      status: selectedPeriod.status || "draft",
      notes: selectedPeriod.notes || "",
    });
  }, [selectedPeriod]);

  useEffect(() => {
    if (!selectedPeriod) {
      setPeriodDetailsOpen(false);
      setPeriodDetailsMinimized(false);
    }
  }, [selectedPeriod]);

  useEffect(() => {
    selectPeriodId("");
    setPeriodDetailsOpen(false);
    setPeriodDetailsMinimized(false);
  }, [clearSelectionSignal, selectPeriodId]);

  useEffect(() => {
    setPeriodRangeStart(masterRangeStart || "");
    setPeriodRangeEnd(masterRangeEnd || "");
  }, [masterRangeEnd, masterRangeSignal, masterRangeStart]);

  useEffect(() => {
    if (!periodWindowDrag.active) {
      return;
    }

    const handleMouseMove = (event) => {
      const panelWidth = PERIOD_WINDOW_WIDTH;
      const maxX = Math.max(12, window.innerWidth - panelWidth - 12);
      const nextX = Math.min(
        maxX,
        Math.max(12, event.clientX - periodWindowDrag.offsetX),
      );
      const nextY = Math.max(12, event.clientY - periodWindowDrag.offsetY);

      setPeriodWindowPos({ x: nextX, y: nextY });
    };

    const handleMouseUp = () => {
      setPeriodWindowDrag((prev) => ({ ...prev, active: false }));
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [periodWindowDrag]);

  useEffect(() => {
    if (!createPeriodWindowDrag.active) {
      return;
    }

    const handleMouseMove = (event) => {
      const panelWidth = PERIOD_WINDOW_WIDTH;
      const maxX = Math.max(12, window.innerWidth - panelWidth - 12);
      const nextX = Math.min(
        maxX,
        Math.max(12, event.clientX - createPeriodWindowDrag.offsetX),
      );
      const nextY = Math.max(
        12,
        event.clientY - createPeriodWindowDrag.offsetY,
      );

      setCreatePeriodWindowPos({ x: nextX, y: nextY });
    };

    const handleMouseUp = () => {
      setCreatePeriodWindowDrag((prev) => ({ ...prev, active: false }));
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [createPeriodWindowDrag]);

  useEffect(() => {
    loadPeriodJobs().catch((err) => {
      setFeedback({
        type: "error",
        message: err.message || "Failed to load jobs for selected period",
      });
    });
  }, [loadPeriodJobs]);

  useEffect(() => {
    loadSelectedJobPeriods().catch((err) => {
      setFeedback({
        type: "error",
        message: err.message || "Failed to load periods for selected job",
      });
    });
  }, [loadSelectedJobPeriods]);

  const mutateAndRefresh = useCallback(
    async (runMutation, successMessage) => {
      try {
        setBusy(true);
        await runMutation();
        await loadPeriods();
        await loadPeriodJobs();
        await loadSelectedJobPeriods();
        if (onAssignmentsChanged) {
          await onAssignmentsChanged();
        }
        setFeedback({ type: "success", message: successMessage });
      } catch (err) {
        setFeedback({
          type: "error",
          message: err.message || "Request failed",
        });
      } finally {
        setBusy(false);
      }
    },
    [loadPeriodJobs, loadPeriods, loadSelectedJobPeriods, onAssignmentsChanged],
  );

  const handleCreatePeriod = async () => {
    if (!canEditRoutes) {
      setFeedback({
        type: "warning",
        message: "You do not have permission to create routes.",
      });
      return;
    }

    if (!parseIdOrNull(newPeriodDraft.assigned_user_id)) {
      setFeedback({
        type: "warning",
        message: "Select an assignee before creating a route.",
      });
      return;
    }

    let createdPeriodId = null;

    await mutateAndRefresh(async () => {
      const payload = {
        assigned_user_id: parseIdOrNull(newPeriodDraft.assigned_user_id),
        period_start: newPeriodDraft.period_start,
        period_end: newPeriodDraft.period_end || null,
        label: newPeriodDraft.label || "default",
        status: newPeriodDraft.status || "draft",
        notes: newPeriodDraft.notes || null,
      };
      const response = await createAssignmentPeriod(payload);
      if (response?.period?.pk_id) {
        createdPeriodId = String(response.period.pk_id);
        selectPeriodId(createdPeriodId);
      }
    }, "Assignment route created.");

    if (createdPeriodId) {
      setCreatePeriodOpen(false);
      setCreatePeriodMinimized(false);
      setPeriodDetailsOpen(true);
      setPeriodDetailsMinimized(false);
    }
  };

  const handleSavePeriodUpdates = async () => {
    if (!selectedPeriodId) {
      setFeedback({ type: "warning", message: "Select a route first." });
      return;
    }

    if (!periodEditDraft.period_start) {
      setFeedback({ type: "warning", message: "Set a start time first." });
      return;
    }

    const jobIds = periodJobs.map((row) => row.job_id).filter(Boolean);
    let estimatedStartForSave = periodEditDraft.period_start;
    let estimatedEndForSave = periodEditDraft.period_end || null;
    let estimatedWindowsForSave = [];

    if (jobIds.length > 0) {
      try {
        const estimateResponse = await estimateAssignmentPeriodEndTime({
          start_time: periodEditDraft.period_start,
          job_ids: jobIds,
        });

        const estimateResult = estimateResponse?.result;
        const breakdown = Array.isArray(estimateResult?.breakdown)
          ? estimateResult.breakdown
          : [];
        estimatedWindowsForSave = breakdown
          .filter((row) => row?.job_id)
          .map((row) => ({
            job_id: row.job_id,
            estimated_start_time: row.estimated_start_time || null,
            estimated_end_time: row.estimated_end_time || null,
          }));
        const windowsByJobId = breakdown.reduce((acc, row) => {
          if (!row?.job_id) {
            return acc;
          }

          acc[String(row.job_id)] = {
            start: row.estimated_start_time || null,
            end: row.estimated_end_time || null,
          };
          return acc;
        }, {});
        setEstimatedJobWindows(windowsByJobId);

        const firstBreakdownRow = breakdown[0] || null;
        if (firstBreakdownRow?.estimated_start_time) {
          estimatedStartForSave = toInputDateTime(
            firstBreakdownRow.estimated_start_time,
          );
        }

        if (estimateResult?.estimated_end_time) {
          estimatedEndForSave = toInputDateTime(
            estimateResult.estimated_end_time,
          );
          setPeriodEditDraft((prev) => ({
            ...prev,
            period_end: estimatedEndForSave,
          }));
        }

        const outsideJobs = Array.isArray(
          estimateResult?.jobs_outside_assignment_day,
        )
          ? estimateResult.jobs_outside_assignment_day
          : [];

        if (outsideJobs.length > 0) {
          const preview = outsideJobs
            .slice(0, 3)
            .map((job) => job.job_number || `Job ${job.job_id}`)
            .join(", ");
          const overflow =
            outsideJobs.length > 3 ? ` and ${outsideJobs.length - 3} more` : "";

          const proceed = window.confirm(
            `Warning: ${outsideJobs.length} job(s) are outside assignment day ${estimateResult.assignment_day_key}. ${preview}${overflow}. Save anyway?`,
          );

          if (!proceed) {
            setFeedback({
              type: "warning",
              message: "Save cancelled due to day mismatch warning.",
            });
            return;
          }

          setPeriodEditDraft((prev) => ({
            ...prev,
            period_start: estimatedStartForSave || prev.period_start,
            period_end: estimatedEndForSave || "",
          }));

          setFeedback({
            type: "info",
            message:
              "Applied estimated times after day mismatch warning. Saving with estimated schedule.",
          });
        }
      } catch (err) {
        setFeedback({
          type: "error",
          message: err.message || "Failed to validate assignment day alignment",
        });
        return;
      }
    }

    await mutateAndRefresh(async () => {
      await updateAssignmentPeriod(selectedPeriodId, {
        assigned_user_id: parseIdOrNull(periodEditDraft.assigned_user_id),
        period_start: estimatedStartForSave || periodEditDraft.period_start,
        period_end: estimatedEndForSave || null,
        label: periodEditDraft.label,
        status: periodEditDraft.status,
        notes: periodEditDraft.notes,
        sync_assignments: true,
        apply_estimated_schedule: estimatedWindowsForSave.length > 0,
        estimated_windows: estimatedWindowsForSave,
      });
    }, "Assignment route updated.");
  };

  // Only adds jobs explicitly checked in the Jobs Queue (not just the
  // currently-clicked/active job) to the selected route.
  const handleAddSelectedJobs = async () => {
    if (!selectedPeriodId) {
      setFeedback({ type: "warning", message: "Select a route first." });
      return;
    }

    const sourceIds = queueSelectedJobIds.map(String);

    if (sourceIds.length === 0) {
      setFeedback({
        type: "warning",
        message: "Check job(s) in the queue first.",
      });
      return;
    }

    const jobsById = new Map(
      visibleJobs
        .filter((job) => job?.pk_id)
        .map((job) => [String(job.pk_id), job]),
    );
    const targetJobs = sourceIds.map((id) => jobsById.get(id)).filter(Boolean);
    const assignableJobs = targetJobs.filter((job) => canAssignToRoute(job));
    const blockedJobs = targetJobs.filter((job) => !canAssignToRoute(job));

    if (assignableJobs.length === 0 || blockedJobs.length > 0) {
      setFeedback({
        type: "warning",
        message: assignRouteStatusWarning,
      });
      return;
    }

    const targetPeriodId = String(selectedPeriodId);
    const targetLabel = `Route #${selectedPeriodId} ${selectedPeriod?.label || "default"}`;
    const jobPeriodResults = await Promise.all(
      assignableJobs.map(async (job) => {
        const jobId = String(job.pk_id);
        const response = await getAssignmentPeriodsByJob(jobId, {
          include_draft: isAdmin,
        });
        const periodsForJob = Array.isArray(response?.periods)
          ? response.periods
          : [];

        return {
          jobId,
          jobLabel: job.job_number || `Job ${jobId}`,
          periodsForJob,
        };
      }),
    );

    const conflicts = jobPeriodResults.filter(({ periodsForJob }) =>
      periodsForJob.some((period) => String(period?.pk_id) !== targetPeriodId),
    );
    const jobIdsToAdd = jobPeriodResults
      .filter(
        ({ periodsForJob }) =>
          !periodsForJob.some(
            (period) => String(period?.pk_id) === targetPeriodId,
          ),
      )
      .map(({ jobId }) => jobId);

    if (jobIdsToAdd.length === 0) {
      setFeedback({
        type: "info",
        message: `Selected job(s) are already assigned to ${targetLabel}.`,
      });
      return;
    }

    if (conflicts.length > 0) {
      const conflictSummary =
        conflicts.length === 1
          ? `${conflicts[0].jobLabel} is already assigned to another route. Move it to ${targetLabel}?`
          : `${conflicts.length} selected job(s) are already assigned to another route. Move them to ${targetLabel}?`;

      const confirmed = window.confirm(conflictSummary);

      if (!confirmed) {
        setFeedback({
          type: "warning",
          message: "Move cancelled.",
        });
        return;
      }
    }

    await mutateAndRefresh(async () => {
      for (const conflict of conflicts) {
        for (const period of conflict.periodsForJob) {
          if (String(period?.pk_id) === targetPeriodId) {
            continue;
          }

          await removeJobFromAssignmentPeriod(period.pk_id, conflict.jobId, {
            resequence: true,
          });
        }
      }

      if (jobIdsToAdd.length === 1) {
        await addJobToAssignmentPeriod(selectedPeriodId, {
          job_id: jobIdsToAdd[0],
          sync_assignments: true,
        });
      } else {
        await addJobsToAssignmentPeriod(selectedPeriodId, jobIdsToAdd, {
          sync_assignments: true,
        });
      }
    }, `${jobIdsToAdd.length} job(s) linked to route.`);
  };

  const handleRemoveJob = async (jobId) => {
    if (!selectedPeriodId || !jobId) {
      return;
    }

    await mutateAndRefresh(async () => {
      await removeJobFromAssignmentPeriod(selectedPeriodId, jobId, {
        resequence: true,
      });
    }, "Job removed from route.");
  };

  const handleMoveJob = async (index, direction) => {
    if (!selectedPeriodId) {
      return;
    }

    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= periodJobs.length) {
      return;
    }

    const reordered = [...periodJobs];
    const [moved] = reordered.splice(index, 1);
    reordered.splice(targetIndex, 0, moved);

    await mutateAndRefresh(async () => {
      await reorderAssignmentPeriodJobs(
        selectedPeriodId,
        reordered.map((row) => row.job_id),
      );
    }, "Route job order updated.");
  };

  const handleEstimatePeriodEnd = async () => {
    if (!selectedPeriodId) {
      setFeedback({ type: "warning", message: "Select a route first." });
      return;
    }

    if (!periodEditDraft.period_start) {
      setFeedback({ type: "warning", message: "Set a start time first." });
      return;
    }

    const jobIds = periodJobs.map((row) => row.job_id).filter(Boolean);
    if (jobIds.length === 0) {
      setFeedback({
        type: "warning",
        message: "No jobs in the selected route to estimate from.",
      });
      return;
    }

    try {
      setBusy(true);
      const response = await estimateAssignmentPeriodEndTime({
        start_time: periodEditDraft.period_start,
        job_ids: jobIds,
      });

      if (response?.result?.estimated_end_time) {
        setPeriodEditDraft((prev) => ({
          ...prev,
          period_end: toInputDateTime(response.result.estimated_end_time),
        }));
      }

      setFeedback({
        type: "success",
        message: "Estimated end time updated from job task durations.",
      });

      const breakdown = Array.isArray(response?.result?.breakdown)
        ? response.result.breakdown
        : [];
      const windowsByJobId = breakdown.reduce((acc, row) => {
        if (!row?.job_id) {
          return acc;
        }

        acc[String(row.job_id)] = {
          start: row.estimated_start_time || null,
          end: row.estimated_end_time || null,
        };
        return acc;
      }, {});
      setEstimatedJobWindows(windowsByJobId);

      const outsideJobs = Array.isArray(
        response?.result?.jobs_outside_assignment_day,
      )
        ? response.result.jobs_outside_assignment_day
        : [];
      if (outsideJobs.length > 0) {
        setFeedback({
          type: "warning",
          message: `${outsideJobs.length} job(s) are outside assignment day ${response.result.assignment_day_key}.`,
        });
      }
    } catch (err) {
      setFeedback({
        type: "error",
        message: err.message || "Failed to estimate end time",
      });
    } finally {
      setBusy(false);
    }
  };

  const handleDeletePeriod = async () => {
    if (!selectedPeriodId) {
      setFeedback({ type: "warning", message: "Select a route first." });
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete assignment route #${selectedPeriodId}? All associated jobs will be unassigned.`,
    );

    if (!confirmed) {
      return;
    }

    await mutateAndRefresh(async () => {
      await deleteAssignmentPeriod(selectedPeriodId);
      selectPeriodId("");
      setPeriodDetailsOpen(false);
      setPeriodDetailsMinimized(false);
      if (onPeriodDeleted) {
        onPeriodDeleted(selectedPeriodId);
      }
    }, "Assignment route deleted.");
  };

  const assigneeOptions = useMemo(
    () =>
      technicianOptions.filter(
        (item) =>
          item.value &&
          item.value !== "__unassigned__" &&
          String(item.role || "").toLowerCase() !== "control",
      ),
    [technicianOptions],
  );

  // A period's route belongs to exactly one customer. An assignee tied to a
  // specific customer (customerPkId set) must match that route's customer;
  // accounts with no customerPkId (internal/global accounts) are always
  // allowed. When the route's customer isn't known (e.g. no route linked
  // yet), nothing is blocked.
  const getAssigneeOptionsForCustomerIds = useCallback(
    (customerIds) => {
      const relevantIds = new Set(
        Array.from(customerIds || [])
          .filter(Boolean)
          .map(String),
      );
      if (relevantIds.size === 0) {
        return assigneeOptions;
      }

      return assigneeOptions.filter((opt) => {
        if (!opt.customerPkId) return true;
        return (
          relevantIds.size === 1 && relevantIds.has(String(opt.customerPkId))
        );
      });
    },
    [assigneeOptions],
  );

  const getAssigneeOptionsForPeriodIds = useCallback(
    (periodIds = []) => {
      const customerIds = new Set();
      periodIds.forEach((id) => {
        const period = periods.find((p) => String(p.pk_id) === String(id));
        if (period?.route_customer_id) {
          customerIds.add(String(period.route_customer_id));
        }
      });
      return getAssigneeOptionsForCustomerIds(customerIds);
    },
    [periods, getAssigneeOptionsForCustomerIds],
  );

  const selectedPeriodAssigneeOptions = useMemo(
    () =>
      getAssigneeOptionsForCustomerIds(
        selectedPeriod?.route_customer_id
          ? [selectedPeriod.route_customer_id]
          : [],
      ),
    [getAssigneeOptionsForCustomerIds, selectedPeriod],
  );

  const bulkAssigneeOptions = useMemo(
    () => getAssigneeOptionsForPeriodIds(selectedPeriodIds),
    [getAssigneeOptionsForPeriodIds, selectedPeriodIds],
  );

  const mergeAssigneeOptions = useMemo(
    () => getAssigneeOptionsForPeriodIds(mergePendingPeriodIds),
    [getAssigneeOptionsForPeriodIds, mergePendingPeriodIds],
  );

  const hasSelectedAssigneeForCreate = Boolean(
    parseIdOrNull(newPeriodDraft.assigned_user_id),
  );

  const handleSelectPeriodFromTable = (periodId) => {
    selectPeriodId(String(periodId));
  };

  const handleTogglePeriodSelection = (period, event) => {
    const periodId = String(period.pk_id);
    const isCtrlLike = Boolean(event?.ctrlKey || event?.metaKey);
    const isShift = Boolean(event?.shiftKey);

    if (isShift) {
      const orderedIds = filteredPeriods.map((item) => String(item.pk_id));
      const anchorId = selectionAnchorPeriodId || periodId;
      const anchorIndex = orderedIds.indexOf(anchorId);
      const clickedIndex = orderedIds.indexOf(periodId);

      if (anchorIndex >= 0 && clickedIndex >= 0) {
        const start = Math.min(anchorIndex, clickedIndex);
        const end = Math.max(anchorIndex, clickedIndex);
        const rangeIds = orderedIds.slice(start, end + 1);

        setSelectedPeriodIds((prev) => {
          const next = new Set(prev.map(String));
          if (isCtrlLike) {
            rangeIds.forEach((id) => {
              if (next.has(id)) {
                next.delete(id);
              } else {
                next.add(id);
              }
            });
          } else {
            rangeIds.forEach((id) => next.add(id));
          }
          return Array.from(next);
        });
      }

      setSelectionAnchorPeriodId(periodId);
      return;
    }

    if (isCtrlLike) {
      setSelectedPeriodIds((prev) => {
        const nextSet = new Set(prev.map(String));
        if (nextSet.has(periodId)) {
          nextSet.delete(periodId);
        } else {
          nextSet.add(periodId);
        }
        return Array.from(nextSet);
      });
      setSelectionAnchorPeriodId(periodId);
      return;
    }

    setSelectedPeriodIds([periodId]);
    setSelectionAnchorPeriodId(periodId);
  };

  const handleToggleCheckboxSelection = (periodId) => {
    setSelectedPeriodIds((prev) => {
      const nextSet = new Set(prev.map(String));
      const id = String(periodId);
      if (nextSet.has(id)) {
        nextSet.delete(id);
      } else {
        nextSet.add(id);
      }
      return Array.from(nextSet);
    });
    setSelectionAnchorPeriodId(String(periodId));
  };

  const handleToggleAllVisiblePeriods = (event) => {
    const checked = Boolean(event.target.checked);
    const nextIds = checked
      ? Array.from(
          new Set([...selectedPeriodIds.map(String), ...paginatedRouteIds]),
        )
      : selectedPeriodIds.filter(
          (periodId) => !paginatedRouteIds.includes(String(periodId)),
        );

    setSelectedPeriodIds(nextIds);
  };

  const handleBulkUpdateSelectedPeriods = async () => {
    const ids = Array.from(
      new Set(selectedPeriodIds.map(String).filter(Boolean)),
    );
    if (ids.length === 0) {
      return;
    }

    const changeStatus = bulkStatus !== BULK_NO_CHANGE;
    const changeAssignee = bulkAssignee !== BULK_NO_CHANGE;

    if (!changeStatus && !changeAssignee) {
      setFeedback({
        type: "warning",
        message: "Choose a bulk status or bulk assignee to apply.",
      });
      return;
    }

    const payload = { sync_assignments: true };
    if (changeStatus) {
      payload.status = bulkStatus;
    }
    if (changeAssignee) {
      payload.assigned_user_id =
        bulkAssignee === BULK_UNASSIGNED ? null : parseIdOrNull(bulkAssignee);
    }

    try {
      setBusy(true);
      for (const periodId of ids) {
        await updateAssignmentPeriod(periodId, payload);
      }
      await refreshAll();
      setSelectedPeriodIds([]);
      setBulkStatus(BULK_NO_CHANGE);
      setBulkAssignee(BULK_NO_CHANGE);
      setFeedback({
        type: "success",
        message: `Updated ${ids.length} route(s).`,
      });
    } catch (err) {
      setFeedback({
        type: "error",
        message: err.message || "Failed to update selected routes",
      });
    } finally {
      setBusy(false);
    }
  };

  // Merges routes that share a route_template into one, reordered by the
  // template's master order. If assignees differ, opens a picker first.
  const handleMergeSelectedPeriods = async (periodIdsArg, targetUserIdArg) => {
    const ids = Array.from(
      new Set((periodIdsArg || selectedPeriodIds).map(String).filter(Boolean)),
    );

    if (ids.length < 2) {
      setFeedback({
        type: "warning",
        message: "Select at least two routes to merge.",
      });
      return;
    }

    const selected = periods.filter((period) =>
      ids.includes(String(period.pk_id)),
    );
    if (selected.some((period) => !period.route_pk_id)) {
      setFeedback({
        type: "error",
        message:
          "All selected routes must be linked to a route template route to merge.",
      });
      return;
    }

    setMerging(true);
    try {
      const targetUserId =
        targetUserIdArg !== undefined
          ? parseIdOrNull(targetUserIdArg)
          : undefined;
      const response = await mergeAssignmentPeriods(ids, targetUserId);
      await refreshAll();
      setSelectedPeriodIds([]);
      setMergePickerOpen(false);
      setMergePendingPeriodIds([]);
      setMergeTargetUserId("");
      setFeedback({
        type: "success",
        message: `Merged ${ids.length} routes into one (${
          response?.job_ids_in_order?.length || 0
        } jobs reordered by master route).`,
      });
    } catch (err) {
      if (err.code === "MULTIPLE_ASSIGNEES") {
        setMergePendingPeriodIds(ids);
        setMergePickerOpen(true);
        setFeedback({
          type: "info",
          message:
            "Selected routes have different assignees. Choose one to continue.",
        });
      } else {
        setFeedback({
          type: "error",
          message: err.message || "Failed to merge selected routes",
        });
      }
    } finally {
      setMerging(false);
    }
  };

  const handleOpenPeriodDetails = (periodId) => {
    selectPeriodId(String(periodId));
    setPeriodDetailsOpen(true);
    setPeriodDetailsMinimized(false);
    setEstimatedJobWindows({});
  };

  const handleApplyPeriodFilter = useCallback(
    async (period) => {
      if (!period || !onPeriodSelectionChange) {
        return;
      }

      try {
        const response = await getAssignmentPeriodJobs(period.pk_id, {
          include_draft: isAdmin,
        });
        const linkedJobs = Array.isArray(response?.jobs) ? response.jobs : [];

        selectPeriodId(String(period.pk_id));
        onPeriodSelectionChange({
          periodId: String(period.pk_id),
          routeName: period.route_name || "",
          label: period.label || "default",
          status: period.status || "draft",
          periodStart: period.period_start || period.created_at || null,
          periodEnd: period.period_end || null,
          jobIds: linkedJobs
            .map((row) => (row?.job_id ? String(row.job_id) : ""))
            .filter(Boolean),
        });
      } catch (err) {
        setFeedback({
          type: "error",
          message: err.message || "Failed to apply route filter",
        });
      }
    },
    [isAdmin, onPeriodSelectionChange],
  );

  const handlePeriodWindowDragStart = (event) => {
    if (event.button !== 0) {
      return;
    }

    setPeriodWindowDrag({
      active: true,
      offsetX: event.clientX - periodWindowPos.x,
      offsetY: event.clientY - periodWindowPos.y,
    });
  };

  const handleCreatePeriodWindowDragStart = (event) => {
    if (event.button !== 0) {
      return;
    }

    setCreatePeriodWindowDrag({
      active: true,
      offsetX: event.clientX - createPeriodWindowPos.x,
      offsetY: event.clientY - createPeriodWindowPos.y,
    });
  };

  const handleOpenCreatePeriodWindow = () => {
    setNewPeriodDraft((prev) => ({
      ...prev,
      period_start: getDefaultCreatePeriodStart(),
    }));
    setCreatePeriodOpen(true);
    setCreatePeriodMinimized(false);
  };

  const handleFeedbackClose = (_, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setFeedback((prev) => ({
      ...prev,
      message: "",
    }));
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{ borderRadius: 4, border: "1px solid", borderColor: "divider" }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.25,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid",
            borderRadius: "15px 15px 0 0",
            borderColor: "divider",
            backgroundColor: "#f5f8fa",
          }}
        >
          <Typography variant="subtitle2" color="text.secondary">
            Job Assignments
          </Typography>
          <Tooltip title="Create route">
            <span>
              <IconButton
                size="small"
                onClick={handleOpenCreatePeriodWindow}
                disabled={!canEditRoutes}
              >
                <AddCircleOutlineOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        <Stack
          spacing={2}
          sx={{
            p: 2,
            "& .MuiTextField-root": {
              "& .MuiInputLabel-root": {
                fontSize: 12,
              },
              "& .MuiInputBase-root": {
                minHeight: 34,
                fontSize: 12,
              },
              "& .MuiInputBase-input": {
                py: 0.65,
              },
            },
          }}
        >
          <Stack
            direction={{ xs: "column", lg: "row" }}
            spacing={1}
            sx={{ alignItems: { lg: "center" }, minWidth: 0 }}
          >
            <Stack
              direction="row"
              spacing={0.5}
              sx={{
                alignItems: "center",
                flex: 1,
                minWidth: 0,
                maxWidth: "100%",
              }}
            >
              <DateRangeFilter
                start={periodRangeStart}
                end={periodRangeEnd}
                onStartChange={setPeriodRangeStart}
                onEndChange={setPeriodRangeEnd}
                onClear={() => {
                  setPeriodRangeStart("");
                  setPeriodRangeEnd("");
                }}
              />
              <TextField
                label="Search Routes"
                size="small"
                value={periodSearch}
                onChange={(event) => setPeriodSearch(event.target.value)}
                placeholder="Search by id, label, assignee, status..."
                fullWidth
                slotProps={{
                  input: {
                    endAdornment:
                      periodSearch.length > 0 ? (
                        <Tooltip title="Clear search">
                          <IconButton
                            size="small"
                            onClick={() => setPeriodSearch("")}
                            sx={{ mr: -0.5 }}
                          >
                            <ClearOutlinedIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      ) : null,
                  },
                }}
              />
              <Tooltip title="Refresh routes">
                <span>
                  <IconButton
                    size="small"
                    onClick={refreshAll}
                    disabled={busy}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1.5,
                    }}
                  >
                    <SyncOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>

          <Divider
            textAlign="left"
            sx={{
              "&::before, &::after": { borderColor: "divider" },
              "& .MuiDivider-wrapper": { px: 1 },
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontSize: 10,
                lineHeight: 1,
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              Filters
            </Typography>
          </Divider>

          <Stack spacing={0.75}>
            <Stack
              direction="row"
              spacing={0.5}
              useFlexGap
              flexWrap="wrap"
              sx={{ alignItems: "center" }}
            >
              {["all", ...PERIOD_STATUS_OPTIONS].map((status) => {
                const selected = periodStatusFilter === status;
                return (
                  <Chip
                    key={status}
                    size="small"
                    label={status === "all" ? "All statuses" : status}
                    onClick={() => setPeriodStatusFilter(status)}
                    variant={selected ? "filled" : "outlined"}
                    sx={{
                      height: 24,
                      fontSize: 11,
                      cursor: "pointer",
                      backgroundColor: selected ? "#e8f2ff" : "#f5f7f8",
                      borderColor: selected ? "#8dbde8" : "#d7dee2",
                      color: selected ? "#1d5f99" : "#42515a",
                      fontWeight: selected ? 700 : 500,
                      "&:hover": {
                        backgroundColor: selected ? "#dbeaff" : "#eef2f4",
                      },
                    }}
                  />
                );
              })}
            </Stack>

            <Stack
              direction="row"
              spacing={0.5}
              useFlexGap
              flexWrap="wrap"
              sx={{ alignItems: "center" }}
            >
              {[
                { value: "all", label: "All assignees" },
                { value: "unassigned", label: "Unassigned" },
                ...periodAssigneeOptions,
              ].map((item) => {
                const selected =
                  String(periodAssigneeFilter) === String(item.value);
                return (
                  <Tooltip key={item.value} title={item.label}>
                    <Chip
                      size="small"
                      label={item.label}
                      onClick={() =>
                        setPeriodAssigneeFilter(String(item.value))
                      }
                      variant={selected ? "filled" : "outlined"}
                      sx={{
                        height: 24,
                        maxWidth: 130,
                        fontSize: 11,
                        cursor: "pointer",
                        backgroundColor: selected ? "#fff7dc" : "#f5f7f8",
                        borderColor: selected ? "#8dbde8" : "#d7dee2",
                        color: selected ? "#7a5b00" : "#42515a",
                        fontWeight: selected ? 700 : 500,
                        "& .MuiChip-label": {
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        },
                        "&:hover": {
                          backgroundColor: selected ? "#dbeaff" : "#eef2f4",
                        },
                      }}
                    />
                  </Tooltip>
                );
              })}
            </Stack>

            <Stack
              direction="row"
              spacing={0.5}
              useFlexGap
              flexWrap="wrap"
              sx={{ alignItems: "center" }}
            >
              {routeFilterOptions.map((item) => {
                const selected = routeAssignmentFilter === item.value;
                return (
                  <Chip
                    key={item.value}
                    size="small"
                    label={item.label}
                    onClick={() => setRouteAssignmentFilter(item.value)}
                    variant={selected ? "filled" : "outlined"}
                    sx={{
                      height: 24,
                      maxWidth: 160,
                      fontSize: 11,
                      cursor: "pointer",
                      backgroundColor: selected ? "#eaf7eb" : "#f5f7f8",
                      borderColor: selected ? "#9fcba8" : "#d7dee2",
                      color: selected ? "#1d6b38" : "#42515a",
                      fontWeight: selected ? 700 : 500,
                      "& .MuiChip-label": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      },
                      "&:hover": {
                        backgroundColor: selected ? "#dff2e4" : "#eef2f4",
                      },
                    }}
                  />
                );
              })}
              <Button
                size="small"
                variant="text"
                onClick={() => {
                  setPeriodSearch("");
                  setPeriodStatusFilter("all");
                  setPeriodAssigneeFilter("all");
                  setRouteAssignmentFilter("all");
                  /* setPeriodRangeStart("");
                setPeriodRangeEnd(""); */
                }}
                disabled={
                  !periodSearch &&
                  periodStatusFilter === "all" &&
                  periodAssigneeFilter === "all" &&
                  routeAssignmentFilter === "all" &&
                  routeAssignmentFilter === "all" &&
                  !periodRangeStart &&
                  !periodRangeEnd
                }
                sx={{
                  minHeight: 30,
                  px: 0.8,
                  py: 0.2,
                  textTransform: "none",
                  fontSize: 11,
                  whiteSpace: "nowrap",
                }}
              >
                Reset Filters
              </Button>
            </Stack>
          </Stack>

          {/* Bulk Actions Section, used to alter item status in bulk. */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1}
            alignItems={{ xs: "stretch", md: "center" }}
            sx={{
              p: 1.25,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              backgroundColor: "#f8fafc",
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              {selectedPeriodCount} route(s) selected
            </Typography>
            <TextField
              select
              size="small"
              label="Bulk status"
              value={bulkStatus}
              onChange={(event) => setBulkStatus(event.target.value)}
              sx={{ minWidth: 120 }}
              SelectProps={selectMenuProps}
              disabled={selectedPeriodCount === 0 || busy}
            >
              <MenuItem value={BULK_NO_CHANGE}>No change</MenuItem>
              {PERIOD_STATUS_OPTIONS.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Bulk assignee"
              value={bulkAssignee}
              onChange={(event) => setBulkAssignee(event.target.value)}
              sx={{ minWidth: 120 }}
              SelectProps={selectMenuProps}
              disabled={selectedPeriodCount === 0 || busy}
            >
              <MenuItem value={BULK_NO_CHANGE}>No change</MenuItem>
              <MenuItem value={BULK_UNASSIGNED}>Unassigned</MenuItem>
              {bulkAssigneeOptions.map((opt) => (
                <MenuItem key={String(opt.value)} value={String(opt.value)}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
            <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
              <Tooltip title="Apply changes to selected routes">
                <span>
                  <IconButton
                    size="small"
                    color="success"
                    onClick={handleBulkUpdateSelectedPeriods}
                    disabled={
                      selectedPeriodCount === 0 ||
                      busy ||
                      (bulkStatus === BULK_NO_CHANGE &&
                        bulkAssignee === BULK_NO_CHANGE)
                    }
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1.5,
                    }}
                  >
                    <SaveOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Merge selected routes into one, reordered by master route">
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleMergeSelectedPeriods()}
                    disabled={selectedPeriodCount < 2 || busy || merging}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1.5,
                    }}
                  >
                    <MergeTypeOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Clear selection">
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setSelectedPeriodIds([])}
                    disabled={selectedPeriodCount === 0 || busy}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1.5,
                    }}
                  >
                    <CancelPresentationOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>

          {pickerActive ? (
            <Alert
              severity="info"
              action={
                <Button
                  size="small"
                  color="inherit"
                  onClick={onCancelPeriodPicker}
                >
                  Cancel
                </Button>
              }
            >
              Select a route below to assign the picked jobs.
            </Alert>
          ) : null}

          <Box sx={{ position: "relative" }}>
            <TablePagination
              component="div"
              count={filteredPeriods.length}
              page={routesPage}
              onPageChange={handleRoutesPageChange}
              rowsPerPage={routesRowsPerPage}
              onRowsPerPageChange={handleRoutesRowsPerPageChange}
              rowsPerPageOptions={[]}
              showFirstButton
              showLastButton
            />

            <TableContainer
              sx={{
                minHeight: ROUTES_TABLE_MIN_HEIGHT_10_ROWS,
                maxHeight: ROUTES_TABLE_MIN_HEIGHT_10_ROWS,
                overflowY: "auto",
              }}
            >
              <Table
                size="small"
                stickyHeader
                sx={{
                  "& .MuiTableCell-root": {
                    px: 0.75,
                    py: 0.6,
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 42 }}>
                      <Checkbox
                        size="small"
                        checked={allVisiblePageSelected}
                        indeterminate={
                          someVisiblePageSelected && !allVisiblePageSelected
                        }
                        onChange={handleToggleAllVisiblePeriods}
                        onClick={(event) => event.stopPropagation()}
                      />
                    </TableCell>
                    <TableCell>Route</TableCell>
                    <TableCell>Assignee</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredPeriods.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="body2" color="text.secondary">
                          No assignment routes match current filters.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedRoutes.map((period) => {
                      const isSelected =
                        String(period.pk_id) === String(selectedPeriodId);
                      const isMultiSelected = selectedPeriodIdsSet.has(
                        String(period.pk_id),
                      );
                      const assigneeId = period.assigned_user_id
                        ? String(period.assigned_user_id)
                        : "";
                      const assigneeLabel = assigneeId
                        ? assigneeLabelById.get(assigneeId) ||
                          period.assigned_username ||
                          period.assigned_user_name ||
                          `User ${assigneeId}`
                        : "Unassigned";
                      const routeLabel = period.route_name || "No Route";
                      const statusTone = getPeriodStatusTone(period.status);

                      return (
                        <TableRow
                          hover
                          key={period.pk_id}
                          selected={isSelected || isMultiSelected}
                          onClick={(event) => {
                            if (pickerActive) {
                              onPeriodPicked?.(period);
                              return;
                            }
                            handleSelectPeriodFromTable(period.pk_id);
                            handleTogglePeriodSelection(period, event);
                          }}
                          onDoubleClick={() =>
                            handleOpenPeriodDetails(period.pk_id)
                          }
                          sx={{
                            cursor: "pointer",
                            "& .period-row-actions": {
                              opacity: isSelected ? 1 : 0,
                              transition: "opacity 120ms ease",
                            },
                            "&:hover .period-row-actions": {
                              opacity: 1,
                            },
                          }}
                        >
                          <TableCell sx={{ width: 42 }}>
                            <Checkbox
                              size="small"
                              checked={isMultiSelected}
                              onClick={(event) => event.stopPropagation()}
                              onChange={(event) => {
                                event.stopPropagation();
                                handleToggleCheckboxSelection(period.pk_id);
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ minWidth: 160 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700 }}
                            >
                              #{period.pk_id} {routeLabel}
                            </Typography>
                            <Stack
                              direction="row"
                              spacing={0.75}
                              useFlexGap
                              flexWrap="wrap"
                              sx={{ mt: 0.25, alignItems: "center" }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {compactDate(period.period_start)}
                              </Typography>
                              <Chip
                                size="small"
                                label={`${period.linked_jobs || 0} jobs`}
                                variant="outlined"
                                color={
                                  period.linked_jobs === 0
                                    ? "warning"
                                    : "default"
                                }
                                sx={{ height: 20, fontSize: 11 }}
                              />
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Tooltip title={assigneeLabel}>
                              {assigneeLabel === "Unassigned" ? (
                                <Chip
                                  size="small"
                                  label="Unassigned"
                                  color="warning"
                                  sx={{ mr: 0.25 }}
                                />
                              ) : (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  noWrap
                                  sx={{
                                    display: "block",
                                    maxWidth: 110,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {assigneeLabel}
                                </Typography>
                              )}
                            </Tooltip>
                          </TableCell>
                          <TableCell
                            sx={{
                              position: "relative",
                              width: 100,
                              maxWidth: 100,
                            }}
                          >
                            <Chip
                              size="small"
                              label={period.status || "draft"}
                              sx={{
                                backgroundColor: statusTone.backgroundColor,
                                color: statusTone.color,
                                fontWeight: 700,
                              }}
                            />
                            <Stack
                              direction="row"
                              spacing={0.25}
                              justifyContent="flex-end"
                              className="period-row-actions"
                              sx={{
                                position: "absolute",
                                top: "50%",
                                right: 6,
                                transform: "translateY(-50%)",
                                px: 0.25,
                                borderRadius: 1.5,
                                backgroundColor: isSelected
                                  ? "rgba(255, 255, 255, 0.86)"
                                  : "rgba(255, 255, 255, 0.86)",
                                boxShadow: isSelected
                                  ? "0 4px 12px rgba(12, 30, 37, 0.14)"
                                  : "none",
                                ".MuiTableRow-hover:hover &": {
                                  backgroundColor: "rgba(245, 248, 250, 0.92)",
                                },
                              }}
                            >
                              <Tooltip title="Open route editor">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleOpenPeriodDetails(period.pk_id);
                                    }}
                                  >
                                    <EditOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Filter queue by this route">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleApplyPeriodFilter(period);
                                    }}
                                  >
                                    <FilterAltOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={filteredPeriods.length}
              page={routesPage}
              onPageChange={handleRoutesPageChange}
              rowsPerPage={routesRowsPerPage}
              onRowsPerPageChange={handleRoutesRowsPerPageChange}
              rowsPerPageOptions={[50, 100]}
              showFirstButton
              showLastButton
            />
          </Box>
        </Stack>

        {periodDetailsOpen && selectedPeriod && !periodDetailsMinimized ? (
          <FloatingPanelWindow
            visible
            onMouseDown={(event) => event.stopPropagation()}
            position={periodWindowPos}
            width={PERIOD_WINDOW_WIDTH}
            zIndex={1700}
            title={`Assignment Route #${selectedPeriod.pk_id}`}
            onHeaderMouseDown={handlePeriodWindowDragStart}
            headerCursor={periodWindowDrag.active ? "grabbing" : "grab"}
            actions={
              <>
                <IconButton
                  size="small"
                  onClick={() => setPeriodDetailsMinimized(true)}
                >
                  <MinimizeOutlinedIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setPeriodDetailsOpen(false)}
                >
                  <CloseOutlinedIcon fontSize="small" />
                </IconButton>
              </>
            }
          >
            <Stack spacing={1.5}>
              <Typography variant="caption" color="text.secondary">
                Created by{" "}
                {selectedPeriod.created_by_username || "System generated"} on{" "}
                {compactDate(selectedPeriod.created_at)}
              </Typography>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                <TextField
                  select
                  size="small"
                  label="Assign To"
                  value={
                    selectedPeriodAssigneeOptions.some(
                      (opt) =>
                        String(opt.value) ===
                        String(periodEditDraft.assigned_user_id),
                    )
                      ? String(periodEditDraft.assigned_user_id)
                      : ""
                  }
                  onChange={(event) =>
                    setPeriodEditDraft((prev) => ({
                      ...prev,
                      assigned_user_id: event.target.value,
                    }))
                  }
                  disabled={!canEditRoutes}
                  sx={{ minWidth: 170 }}
                  SelectProps={selectMenuProps}
                >
                  <MenuItem value="">Unassigned</MenuItem>
                  {selectedPeriodAssigneeOptions.map((opt) => (
                    <MenuItem key={String(opt.value)} value={String(opt.value)}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  size="small"
                  label="Label"
                  value={periodEditDraft.label}
                  onChange={(event) =>
                    setPeriodEditDraft((prev) => ({
                      ...prev,
                      label: event.target.value,
                    }))
                  }
                  sx={{ minWidth: 100 }}
                />
                <TextField
                  select
                  size="small"
                  label="Status"
                  value={periodEditDraft.status}
                  onChange={(event) =>
                    setPeriodEditDraft((prev) => ({
                      ...prev,
                      status: event.target.value,
                    }))
                  }
                  disabled={!canEditRoutes}
                  sx={{ minWidth: 110 }}
                  SelectProps={selectMenuProps}
                >
                  {PERIOD_STATUS_OPTIONS.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </TextField>
                <Tooltip
                  title={
                    hasPeriodEditChanges
                      ? "Save changes to this route"
                      : "No changes to save"
                  }
                >
                  <span>
                    <IconButton
                      variant="outlined"
                      color="success"
                      onClick={handleSavePeriodUpdates}
                      disabled={
                        !selectedPeriodId ||
                        !canEditRoutes ||
                        busy ||
                        !hasPeriodEditChanges
                      }
                    >
                      <SaveOutlinedIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>

              <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                <TextField
                  size="small"
                  type="datetime-local"
                  label="Start"
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                  value={periodEditDraft.period_start}
                  onChange={(event) =>
                    setPeriodEditDraft((prev) => ({
                      ...prev,
                      period_start: event.target.value,
                    }))
                  }
                  sx={{ minWidth: 170 }}
                />
                <TextField
                  size="small"
                  type="datetime-local"
                  label="End"
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                  value={periodEditDraft.period_end}
                  onChange={(event) =>
                    setPeriodEditDraft((prev) => ({
                      ...prev,
                      period_end: event.target.value,
                    }))
                  }
                  sx={{ minWidth: 170 }}
                />
              </Stack>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <Tooltip title="Update estimated job windows">
                  <span>
                    <IconButton
                      variant="outlined"
                      onClick={handleEstimatePeriodEnd}
                      disabled={!selectedPeriodId || !canEditRoutes || busy}
                    >
                      <UpdateOutlinedIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Delete this route">
                  <span>
                    <IconButton
                      variant="outlined"
                      color="error"
                      onClick={handleDeletePeriod}
                      disabled={!selectedPeriodId || !isAdmin || busy}
                    >
                      <DeleteForeverOutlinedIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Button
                  variant="text"
                  onClick={handleAddSelectedJobs}
                  disabled={
                    !selectedPeriodId ||
                    queueSelectedJobIds.length === 0 ||
                    !canEditRoutes ||
                    busy
                  }
                >
                  Add Selected Job/s ({queueSelectedJobIds.length})
                </Button>
              </Stack>

              <Divider />

              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                  {`Notes: ${selectedPeriod?.notes || "-"}`}
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle2">
                  Jobs In Selected Route
                </Typography>
                <Chip
                  size="small"
                  label={`${selectedPeriod.linked_jobs || 0} jobs`}
                />
              </Stack>
              {periodJobs.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No jobs linked to the selected route.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {periodJobs.map((row, index) => (
                    <Box
                      key={`${row.job_id}-${index}`}
                      sx={{
                        display: "flex",
                        gap: 1,
                        alignItems: "center",
                        justifyContent: "space-between",
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                        px: 1.25,
                        py: 0.75,
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip size="small" label={`#${index + 1}`} />
                        <Stack direction="column" spacing={0}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: getRoomRestrictionColor(
                                row.room_restriction,
                              ),
                            }}
                          >
                            {row.room_name}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 200, fontSize: 12 }}
                          >
                            {row.job_number}
                          </Typography>
                        </Stack>

                        <Tooltip title="Number of tasks in this job">
                          <Chip
                            variant="outlined"
                            color={
                              row.total_job_tasks > 0 ? "primary" : "warning"
                            }
                            label={row.total_job_tasks}
                          />
                        </Tooltip>
                        <Tooltip title="Current status of this job">
                          <Chip
                            variant="caption"
                            color="text.secondary"
                            label={row.status}
                          />
                        </Tooltip>
                        <Typography variant="caption" color="text.secondary">
                          {compactDate(row.scheduled_start)}
                        </Typography>

                        <Stack spacing={0}>
                          {estimatedJobWindows[String(row.job_id)]?.start ? (
                            <Typography variant="caption" color="primary.main">
                              Est:{" "}
                              {compactDate(
                                estimatedJobWindows[String(row.job_id)].start,
                              )}{" "}
                              -{" "}
                              {compactDate(
                                estimatedJobWindows[String(row.job_id)].end,
                              )}
                            </Typography>
                          ) : null}
                        </Stack>
                      </Stack>

                      <Stack direction="row" spacing={0.25}>
                        <Tooltip title="Move up">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleMoveJob(index, -1)}
                              disabled={index === 0 || busy || !canEditRoutes}
                            >
                              <ArrowUpwardOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Move down">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleMoveJob(index, 1)}
                              disabled={
                                index === periodJobs.length - 1 ||
                                busy ||
                                !canEditRoutes
                              }
                            >
                              <ArrowDownwardOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Remove job from route">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveJob(row.job_id)}
                              disabled={busy || !canEditRoutes}
                            >
                              <DeleteOutlineOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}
            </Stack>
          </FloatingPanelWindow>
        ) : null}

        {createPeriodOpen && !createPeriodMinimized ? (
          <FloatingPanelWindow
            visible
            onMouseDown={(event) => event.stopPropagation()}
            position={createPeriodWindowPos}
            width={PERIOD_WINDOW_WIDTH}
            zIndex={1695}
            title="Create Assignment Route"
            onHeaderMouseDown={handleCreatePeriodWindowDragStart}
            headerCursor={createPeriodWindowDrag.active ? "grabbing" : "grab"}
            actions={
              <>
                <IconButton
                  size="small"
                  onClick={() => setCreatePeriodMinimized(true)}
                >
                  <MinimizeOutlinedIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setCreatePeriodOpen(false)}
                >
                  <CloseOutlinedIcon fontSize="small" />
                </IconButton>
              </>
            }
          >
            <Stack spacing={1.25}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                <TextField
                  select
                  size="small"
                  label="Assign To"
                  value={
                    assigneeOptions.some(
                      (opt) =>
                        String(opt.value) ===
                        String(newPeriodDraft.assigned_user_id),
                    )
                      ? String(newPeriodDraft.assigned_user_id)
                      : ""
                  }
                  onChange={(event) =>
                    setNewPeriodDraft((prev) => ({
                      ...prev,
                      assigned_user_id: event.target.value,
                    }))
                  }
                  sx={{ minWidth: 170 }}
                  SelectProps={selectMenuProps}
                >
                  <MenuItem value="">Unassigned</MenuItem>
                  {assigneeOptions.map((opt) => (
                    <MenuItem key={String(opt.value)} value={String(opt.value)}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  size="small"
                  label="Label"
                  value={newPeriodDraft.label}
                  onChange={(event) =>
                    setNewPeriodDraft((prev) => ({
                      ...prev,
                      label: event.target.value,
                    }))
                  }
                  sx={{ minWidth: 100 }}
                />
                <TextField
                  select
                  size="small"
                  label="Status"
                  value={newPeriodDraft.status}
                  onChange={(event) =>
                    setNewPeriodDraft((prev) => ({
                      ...prev,
                      status: event.target.value,
                    }))
                  }
                  sx={{ minWidth: 110 }}
                  slotProps={{
                    select: selectMenuProps,
                  }}
                >
                  {PERIOD_STATUS_OPTIONS.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>

              <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                <TextField
                  size="small"
                  type="datetime-local"
                  label="Start"
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                  value={newPeriodDraft.period_start}
                  onChange={(event) =>
                    setNewPeriodDraft((prev) => ({
                      ...prev,
                      period_start: event.target.value,
                    }))
                  }
                  sx={{ minWidth: 170 }}
                />
                <TextField
                  size="small"
                  type="datetime-local"
                  label="End"
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                  value={newPeriodDraft.period_end}
                  onChange={(event) =>
                    setNewPeriodDraft((prev) => ({
                      ...prev,
                      period_end: event.target.value,
                    }))
                  }
                  sx={{ minWidth: 170 }}
                />
              </Stack>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                {hasSelectedAssigneeForCreate ? (
                  <Button
                    variant="contained"
                    onClick={handleCreatePeriod}
                    disabled={!canEditRoutes || busy}
                  >
                    Create Route
                  </Button>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Select an assignee to enable route creation.
                  </Typography>
                )}
              </Stack>
            </Stack>
          </FloatingPanelWindow>
        ) : null}

        {periodDetailsOpen && selectedPeriod && periodDetailsMinimized ? (
          <FloatingPanelDockTray
            zIndex={1600}
            bottom={hasMinimizedJobWindows ? 58 : 12}
          >
            <FloatingPanelDock
              label={`Route #${selectedPeriod.pk_id}`}
              indicatorColor="warning.main"
              zIndex={1600}
              onRestore={() => setPeriodDetailsMinimized(false)}
              onClose={() => setPeriodDetailsOpen(false)}
            />
          </FloatingPanelDockTray>
        ) : null}

        {createPeriodOpen && createPeriodMinimized ? (
          <FloatingPanelDockTray
            zIndex={1600}
            bottom={hasMinimizedJobWindows ? 104 : 58}
          >
            <FloatingPanelDock
              label="Create Route"
              indicatorColor="primary.main"
              zIndex={1600}
              onRestore={() => setCreatePeriodMinimized(false)}
              onClose={() => setCreatePeriodOpen(false)}
            />
          </FloatingPanelDockTray>
        ) : null}
      </Paper>

      <Snackbar
        open={Boolean(feedback.message)}
        autoHideDuration={3000}
        onClose={handleFeedbackClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        sx={{ zIndex: 5000 }}
      >
        <Alert
          onClose={handleFeedbackClose}
          severity={feedback.type || "info"}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={mergePickerOpen}
        onClose={() => (!merging ? setMergePickerOpen(false) : null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Choose merged assignee</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            The selected routes have different assignees. Pick one user to
            assign to the merged route.
          </Typography>
          <TextField
            select
            size="small"
            label="Assignee"
            fullWidth
            value={mergeTargetUserId}
            onChange={(event) => setMergeTargetUserId(event.target.value)}
            slotProps={{
              select: selectMenuProps,
            }}
          >
            {mergeAssigneeOptions.map((opt) => (
              <MenuItem key={String(opt.value)} value={String(opt.value)}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMergePickerOpen(false)} disabled={merging}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={merging || !mergeTargetUserId}
            onClick={() =>
              handleMergeSelectedPeriods(
                mergePendingPeriodIds,
                mergeTargetUserId,
              )
            }
          >
            Merge
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
